// File: src/components/LinkShopifyAccount.jsx

import React, { useContext, useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { Spinner, Card, Button, Text, BlockStack, InlineStack, Banner } from '@shopify/polaris';
import { jwtDecode } from 'jwt-decode';

const API = process.env.REACT_APP_API_URL || 'https://api.baobrain.com';

function tokenIsExpired(token) {
  if (!token) return true;
  try {
    const decoded = jwtDecode(token);
    return Date.now() >= (decoded.exp * 1000) - 5000;
  } catch (e) {
    return true;
  }
}

export default function LinkShopifyAccount() {
  const { token, setToken, setUser } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [hasRedirected, setHasRedirected] = useState(false);

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const shop = params.get('shop');
  const site_id = params.get('site_id');
  const site_token = params.get('site_token');
  const access_token = params.get('access_token');
  const host = params.get('host');
  
  const isEmbedded = !!host;

  const currentPathAndQuery = useMemo(() => 
    `${location.pathname}${location.search}`, 
    [location.pathname, location.search]
  );

  console.log('[LinkShopifyAccount] Params:', {
    shop,
    site_id,
    site_token,
    hasAccessToken: !!access_token,
    hasToken: !!token,
    tokenExpired: token ? tokenIsExpired(token) : 'no token',
    host,
    isEmbedded
  });

  useEffect(() => {
    if (hasRedirected) {
      console.log('[LinkShopifyAccount] Already redirected, preventing loop');
      return;
    }

    // CASE 1: Fresh install - redirect to connect-shopify
    if (shop && !site_id && !site_token && !access_token) {
      console.log('[LinkShopifyAccount] Fresh install - redirecting to connect-shopify');
      setHasRedirected(true);
      navigate(`/connect-shopify?shop=${shop}${host ? `&host=${host}` : ''}`, { replace: true });
      return;
    }

    // CASE 2: Have all OAuth params - proceed to link
    if (shop && site_id && site_token && access_token) {
      
      // CRITICAL FIX: For embedded apps, we DON'T require a JWT token
      // The backend will handle authentication via session tokens
      if (isEmbedded) {
        console.log('[LinkShopifyAccount] ✅ Embedded OAuth complete - proceeding without JWT check');
      } else if (!token || tokenIsExpired(token)) {
        // Non-embedded: require JWT
        console.log('[LinkShopifyAccount] Non-embedded needs JWT - redirecting to login');
        setToken(null);
        sessionStorage.removeItem('jwtToken');
        localStorage.removeItem('jwtToken');
        setHasRedirected(true);
        navigate(`/login?return_to=${encodeURIComponent(currentPathAndQuery)}`, { replace: true });
        return;
      }
      
      async function claimStore() {
        try {
          setLoading(true);
          const query = new URLSearchParams({ shop, site_id, site_token, access_token });
          
          console.log('[LinkShopifyAccount] 🔗 Calling /shopify/link...');
          
          //const res = await fetch(`${API}/shopify/link?${query.toString()}`, {
            let res = await fetch(`${API}/shopify/link?${query.toString()}`, {
            method: 'POST',
            headers: {
              'Authorization': token ? `Bearer ${token}` : '',
              'Content-Type': 'application/json',
            },
          });

          // Handle 401 - try to authenticate with session token
          if (res.status === 401 && isEmbedded) {
            console.warn('[LinkShopifyAccount] 401 in embedded mode - attempting session auth');
            
            try {
              // Get session token from App Bridge (CDN version)
              const shopify = window.shopify || window.app;
              if (!shopify) {
                throw new Error('App Bridge not initialized');
              }

              // CDN App Bridge: Use idToken() method to get session token
              const sessionToken = await shopify.idToken();
              
              // Try session auth
              const authRes = await fetch(`${API}/shopify/session-auth`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${sessionToken}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ shop, host })
              });

              if (authRes.ok) {
                const authData = await authRes.json();
                
                // Store the new token
                setToken(authData.access_token);
                sessionStorage.setItem('jwtToken', authData.access_token);
                
                // Set user data
                setUser({
                  id: authData.user_id,
                  email: authData.email,
                  shop: authData.shop,
                  isEmbedded: true
                });
                
                console.log('✅ Session auth successful, retrying link...');
                
                // Retry the link with new token
                const retryRes = await fetch(`${API}/shopify/link?${query.toString()}`, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${authData.access_token}`,
                    'Content-Type': 'application/json',
                  },
                });

                if (!retryRes.ok) {
                  throw new Error('Link failed after session auth');
                }

                // Continue to success
                res = retryRes;
                
              } else {
                throw new Error('Session auth failed');
              }
              
            } catch (authError) {
              console.error('Session auth error:', authError);
              
              // Try fallback auth
              const fallbackRes = await fetch(`${API}/shopify/session-auth-fallback`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ shop, host })
              });

              if (fallbackRes.ok) {
                const fallbackData = await fallbackRes.json();
                setToken(fallbackData.access_token);
                sessionStorage.setItem('jwtToken', fallbackData.access_token);
                setUser({
                  id: fallbackData.user_id,
                  email: fallbackData.email,
                  shop: fallbackData.shop,
                  isEmbedded: true
                });
                
                // Retry link with fallback token
                const retryRes = await fetch(`${API}/shopify/link?${query.toString()}`, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${fallbackData.access_token}`,
                    'Content-Type': 'application/json',
                  },
                });

                if (!retryRes.ok) {
                  throw new Error('Link failed after fallback auth');
                }
              } else {
                throw new Error('Both session and fallback auth failed');
              }
            }
          } else if (res.status === 401) {
            // Non-embedded 401 - redirect to login
            console.warn('[LinkShopifyAccount] 401 non-embedded - redirecting to login');
            if (!isEmbedded) {
              setToken(null);
              sessionStorage.removeItem('jwtToken');
              localStorage.removeItem('jwtToken');
            }
            setHasRedirected(true);
            navigate(`/login?return_to=${encodeURIComponent(currentPathAndQuery)}`, { replace: true });
            return;
          }

          // Handle 409 - conflict
          if (res.status === 409) {
            const body = await res.json().catch(() => ({}));
            const detail = (body.detail || '').toLowerCase();

            if (detail.includes('already been claimed')) {
              setError('CLAIMED_BY_OTHER');
            } else if (detail.includes('one site per account') || detail.includes('site limit')) {
              setError('ONE_SITE_LIMIT');
            } else {
              setError(body.detail || 'Connection conflict (409)');
            }

            setLoading(false);
            return;
          }

          // Handle other errors
          if (!res.ok) {
            const payload = await res.json().catch(() => ({}));
            throw new Error(payload.detail || `Failed to link store (HTTP ${res.status})`);
          }

          // SUCCESS!
          console.log('[LinkShopifyAccount] ✅ Link successful');
          
          sessionStorage.setItem(`shop_${shop}_setup`, 'complete');
          sessionStorage.setItem(`shop_${shop}_site_id`, site_id);
          sessionStorage.setItem(`shop_${shop}_site_token`, site_token);

          const successUrl = `/ConnectSuccessPage?` +
            `shop=${encodeURIComponent(shop)}&` +
            `connected=true&` +
            `site_id=${site_id}&` +
            `site_token=${site_token}` +
            (host ? `&host=${encodeURIComponent(host)}` : '');

          setHasRedirected(true);
          navigate(successUrl, { replace: true });

        } catch (err) {
          console.error('[LinkShopifyAccount] ❌ Error:', err);
          setError(err.message || 'Unknown error occurred');
          setLoading(false);
        }
      }

      claimStore();
      return;
    }

    // CASE 3: Incomplete params
    if (shop && (!site_id || !site_token || !access_token)) {
      console.warn('[LinkShopifyAccount] Incomplete params - redirecting');
      setHasRedirected(true);
      navigate(`/connect-shopify?shop=${shop}${host ? `&host=${host}` : ''}`, { replace: true });
      return;
    }

    // CASE 4: No shop param
    if (!shop) {
      console.warn('[LinkShopifyAccount] No shop param - redirecting home');
      setHasRedirected(true);
      navigate('/home', { replace: true });
      return;
    }

    setLoading(false);

  }, [
    shop,
    site_id,
    site_token,
    access_token,
    host,
    isEmbedded,
    token,
    navigate,
    setToken,
    setUser,
    currentPathAndQuery,
    hasRedirected
  ]);

  // LOADING STATE
  if (loading && !error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <Spinner size="large" />
        <Text as="p" variant="bodyLg">Linking your Shopify store...</Text>
        <Text as="p" variant="bodySm" tone="subdued">This should only take a moment</Text>
      </div>
    );
  }

  // ERROR STATE
  if (error) {
    const isClaimedByOther = 
      error === 'CLAIMED_BY_OTHER' ||
      (typeof error === 'string' && error.toLowerCase().includes('claimed by another user'));

    const isOneSiteLimit = 
      error === 'ONE_SITE_LIMIT' ||
      (typeof error === 'string' && error.toLowerCase().includes('one site per account'));

    const title = isClaimedByOther
      ? 'Store Already Claimed'
      : isOneSiteLimit
      ? 'Site Limit Reached'
      : 'Connection Failed';

    const message = isClaimedByOther
      ? 'This Shopify store has already been claimed by another BaoBrain account.'
      : isOneSiteLimit
      ? 'You can only connect one site per account. To replace your current site, go to Settings and disconnect it first.'
      : (typeof error === 'string' ? error : 'We could not complete the connection.');

    return (
      <div style={{ maxWidth: '700px', margin: '50px auto', padding: '20px' }}>
        <Card>
          <BlockStack gap="400">
            <Banner tone="critical" title={title}>
              <Text as="p">{message}</Text>
            </Banner>

            <InlineStack gap="200" align="center">
              {!isClaimedByOther && !isOneSiteLimit && (
                <>
                  <Button variant="primary" onClick={() => window.location.reload()}>
                    Try Again
                  </Button>
                  <Button onClick={() => navigate('/home')}>
                    Go to Home
                  </Button>
                </>
              )}
            </InlineStack>
          </BlockStack>
        </Card>
      </div>
    );
  }

  return null;
}